import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FileText, Users, Eye, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statIconStyles = {
  blue: 'bg-blue-50 border-blue-200 text-blue-600',
  violet: 'bg-violet-50 border-violet-200 text-violet-600',
  slate: 'bg-slate-100 border-slate-200 text-slate-700',
  green: 'bg-green-50 border-green-200 text-green-600'
};

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
    setLoading(false);
  };

  const statCards = stats ? [
    { label: 'Total Leads', value: stats.leads_count + stats.contacts_count, icon: Users, color: 'blue' },
    { label: 'Published Posts', value: stats.posts_count, icon: FileText, color: 'violet' },
    { label: 'Draft Posts', value: stats.drafts_count, icon: FileText, color: 'slate' },
    { label: 'Total Views', value: stats.total_views, icon: Eye, color: 'green' }
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to your admin panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const iconWrap = statIconStyles[stat.color] || statIconStyles.slate;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`stat-card-${index}`}
              className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-6 rounded-2xl"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${iconWrap}`}>
                  <Icon size={24} />
                </div>
                <TrendingUp size={18} className="text-green-600" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-1">{stat.value}</div>
              <div className="text-muted-foreground text-sm">{stat.label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Recent Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-6 rounded-2xl"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">Recent Leads</h2>
            <Link
              to="/admin/leads"
              data-testid="view-all-leads"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View All
            </Link>
          </div>

          {recentLeads.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No leads yet</p>
          ) : (
            <div className="space-y-4">
              {recentLeads.map((lead, index) => (
                <div
                  key={index}
                  data-testid={`recent-lead-${index}`}
                  className="bg-muted/50 border border-[#E2E8F0] p-4 rounded-lg"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-foreground font-medium">{lead.name}</p>
                      <p className="text-muted-foreground text-sm">{lead.email}</p>
                    </div>
                    <span className="bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-xs text-blue-700">
                      {lead.status}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm line-clamp-2">{lead.message}</p>
                  <p className="text-muted-foreground text-xs mt-2">
                    {new Date(lead.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-6 rounded-2xl"
        >
          <h2 className="text-xl font-semibold text-foreground mb-6">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/admin/blog/new"
              data-testid="quick-new-post"
              className="block bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-3 font-semibold text-center transition-colors"
            >
              Create New Blog Post
            </Link>
            <Link
              to="/admin/blog"
              data-testid="quick-manage-posts"
              className="block bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-6 py-3 font-semibold text-center transition-colors"
            >
              Manage Blog Posts
            </Link>
            <Link
              to="/admin/leads"
              data-testid="quick-view-leads"
              className="block bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-6 py-3 font-semibold text-center transition-colors"
            >
              View All Leads
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-[#E2E8F0]">
            <h3 className="text-foreground font-medium mb-3">Helpful Links</h3>
            <div className="space-y-2">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-blue-600 hover:text-blue-700 text-sm"
              >
                View Live Website →
              </a>
              <a
                href="/blog"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-blue-600 hover:text-blue-700 text-sm"
              >
                View Blog →
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
